import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "utils/redux/store";
import { request } from "utils/request";
import { toast } from "react-toastify";
import { successNotificationOptions } from "utils/constants";
import { REACT_APP_API_URL } from "utils/environmentVariables";
import { FileSyncConfig, FileSyncConfigPayload, FileSyncLog } from "./types";
import { responseStatus } from "utils/types";

// ------------------ State Type/Structure ------------------
export interface FileSyncState {
  config: FileSyncConfig | null;
  logs: FileSyncLog[];
  quarantinedLogs: FileSyncLog[];
  currentLogDetails: FileSyncLog | null;

  getConfigStatus: responseStatus;
  createConfigStatus: responseStatus;
  updateConfigStatus: responseStatus;
  getLogsStatus: responseStatus;
  getLogDetailsStatus: responseStatus;
  approveQuarantineStatus: responseStatus;
  rejectQuarantineStatus: responseStatus;
}

const initialState: FileSyncState = {
  config: null,
  logs: [],
  quarantinedLogs: [],
  currentLogDetails: null,

  getConfigStatus: "idle",
  createConfigStatus: "idle",
  updateConfigStatus: "idle",
  getLogsStatus: "idle",
  getLogDetailsStatus: "idle",
  approveQuarantineStatus: "idle",
  rejectQuarantineStatus: "idle",
};

// ------------------ Async Thunks ------------------

export const getFileSyncConfig = createAsyncThunk(
  "fileSync/getConfig",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/fileSync/config`;
    const response = (await request(requestUrl)) as {
      config: FileSyncConfig | null;
    };
    return response.config;
  }
);

export const createFileSyncConfig = createAsyncThunk(
  "fileSync/createConfig",
  async (payload: FileSyncConfigPayload) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/fileSync/config`;
    const response = (await request(requestUrl, {
      method: "POST",
      body: JSON.stringify(payload),
    })) as FileSyncConfig;
    return response;
  }
);

export const updateFileSyncConfig = createAsyncThunk(
  "fileSync/updateConfig",
  async (payload: FileSyncConfigPayload) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/fileSync/config`;
    const response = (await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify(payload),
    })) as FileSyncConfig;
    return response;
  }
);

export const getFileSyncLogs = createAsyncThunk(
  "fileSync/getLogs",
  async (status?: string) => {
    let requestUrl = `${REACT_APP_API_URL}/talentInsights/fileSync/logs`;
    if (status) {
      requestUrl += `?status=${status}`;
    }
    const response = (await request(requestUrl)) as FileSyncLog[];
    return { logs: response, status };
  }
);

export const getFileSyncLogDetails = createAsyncThunk(
  "fileSync/getLogDetails",
  async (logId: number) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/fileSync/logs/${logId}`;
    const response = (await request(requestUrl)) as FileSyncLog;
    return response;
  }
);

export const approveQuarantinedSync = createAsyncThunk(
  "fileSync/approveQuarantine",
  async (logId: number, { dispatch }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/fileSync/logs/${logId}/approve`;
    const response = (await request(requestUrl, {
      method: "POST",
    })) as FileSyncLog;
    dispatch(updateFileSyncLog(response));
    return response;
  }
);

export const rejectQuarantinedSync = createAsyncThunk(
  "fileSync/rejectQuarantine",
  async (
    { logId, reason }: { logId: number; reason?: string },
    { dispatch }
  ) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/fileSync/logs/${logId}/reject`;
    const response = (await request(requestUrl, {
      method: "POST",
      body: JSON.stringify({ reason }),
    })) as FileSyncLog;
    dispatch(updateFileSyncLog(response));
    return response;
  }
);

// ------------------ Slice ------------------
export const fileSyncSlice = createSlice({
  name: "fileSync",
  initialState,
  reducers: {
    resetFileSyncState: () => initialState,
    clearCurrentLogDetails: (state) => {
      state.currentLogDetails = null;
    },
    updateFileSyncLog: (state, action: PayloadAction<FileSyncLog>) => {
      const existingIndex = state.logs.findIndex(
        (log) => log.id === action.payload.id
      );
      if (existingIndex === -1) {
        state.logs.push(action.payload);
      } else {
        state.logs[existingIndex] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Config
      .addCase(getFileSyncConfig.pending, (state) => {
        state.getConfigStatus = "loading";
      })
      .addCase(getFileSyncConfig.fulfilled, (state, action) => {
        state.getConfigStatus = "succeeded";
        state.config = action.payload;
      })
      .addCase(getFileSyncConfig.rejected, (state) => {
        state.getConfigStatus = "failed";
      })
      .addCase(createFileSyncConfig.pending, (state) => {
        state.createConfigStatus = "loading";
      })
      .addCase(createFileSyncConfig.fulfilled, (state, action) => {
        state.createConfigStatus = "succeeded";
        state.config = action.payload;
        toast.success(
          "Configuration created successfully",
          successNotificationOptions
        );
      })
      .addCase(createFileSyncConfig.rejected, (state) => {
        state.createConfigStatus = "failed";
      })
      .addCase(updateFileSyncConfig.pending, (state) => {
        state.updateConfigStatus = "loading";
      })
      .addCase(updateFileSyncConfig.fulfilled, (state, action) => {
        state.updateConfigStatus = "succeeded";
        state.config = { ...state.config, ...action.payload };
        toast.success(
          "Configuration updated successfully",
          successNotificationOptions
        );
      })
      .addCase(updateFileSyncConfig.rejected, (state) => {
        state.updateConfigStatus = "failed";
      })
      .addCase(getFileSyncLogs.pending, (state) => {
        state.getLogsStatus = "loading";
      })
      .addCase(getFileSyncLogs.fulfilled, (state, action) => {
        state.getLogsStatus = "succeeded";
        if (action.payload.status === "quarantined") {
          state.quarantinedLogs = action.payload.logs;
        } else {
          state.logs = action.payload.logs;
        }
      })
      .addCase(getFileSyncLogs.rejected, (state) => {
        state.getLogsStatus = "failed";
      })
      .addCase(getFileSyncLogDetails.pending, (state) => {
        state.getLogDetailsStatus = "loading";
      })
      .addCase(getFileSyncLogDetails.fulfilled, (state, action) => {
        state.getLogDetailsStatus = "succeeded";
        state.currentLogDetails = action.payload;
      })
      .addCase(getFileSyncLogDetails.rejected, (state) => {
        state.getLogDetailsStatus = "failed";
      })
      .addCase(approveQuarantinedSync.pending, (state) => {
        state.approveQuarantineStatus = "loading";
      })
      .addCase(approveQuarantinedSync.fulfilled, (state, action) => {
        state.approveQuarantineStatus = "succeeded";
        toast.success("Sync approved successfully", successNotificationOptions);
      })
      .addCase(approveQuarantinedSync.rejected, (state) => {
        state.approveQuarantineStatus = "failed";
      })
      .addCase(rejectQuarantinedSync.pending, (state) => {
        state.rejectQuarantineStatus = "loading";
      })
      .addCase(rejectQuarantinedSync.fulfilled, (state, action) => {
        state.rejectQuarantineStatus = "succeeded";
        toast.success("Sync rejected successfully", successNotificationOptions);
      })
      .addCase(rejectQuarantinedSync.rejected, (state) => {
        state.rejectQuarantineStatus = "failed";
      });
  },
});

// ------------------ Actions ------------------
export const { clearCurrentLogDetails, resetFileSyncState, updateFileSyncLog } =
  fileSyncSlice.actions;

// ------------------ Selectors ------------------
export const selectFileSyncConfig = (state: RootState) => state.fileSync.config;
export const selectFileSyncLogs = (state: RootState) => state.fileSync.logs;
export const selectCurrentLogDetails = (state: RootState) =>
  state.fileSync.currentLogDetails;
export const selectGetConfigStatus = (state: RootState) =>
  state.fileSync.getConfigStatus;
export const selectUpdateConfigStatus = (state: RootState) =>
  state.fileSync.updateConfigStatus;
export const selectGetLogsStatus = (state: RootState) =>
  state.fileSync.getLogsStatus;
export const selectApproveQuarantineStatus = (state: RootState) =>
  state.fileSync.approveQuarantineStatus;
export const selectRejectQuarantineStatus = (state: RootState) =>
  state.fileSync.rejectQuarantineStatus;
export const selectIsConfigActive = (state: RootState) =>
  state.fileSync.config?.is_active ?? false;

export const selectLastSuccessfulSync = (state: RootState) =>
  state.fileSync.config?.last_successful_sync_at ?? null;

export default fileSyncSlice.reducer;
